import React, { useEffect, useState } from "react";
import {
    Modal,
    Box,
    Typography,
    Button,
    Grid,
    TextField,
    CircularProgress,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    FormHelperText,
} from "@mui/material";
import { createAdmin, editAdmin } from "../../../services/adminServices/AdminServices";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const INITIAL_FORM_STATE = {
    name: '',
    email: '',
    mobile: '',
    role: '',
};

const roleLabels = {
    super_admin: "Super Admin",
    coe_admin: "Coe Admin",
    coe_report_admin: "Report Admin",
    coe_report_viewer: "Report Viewer",
};

const roles = Object.keys(roleLabels);

const EditAdminModal = ({ open, onClose, title, rowData, getAllUsersData, allCOE }) => {
    const [formValues, setFormValues] = useState(INITIAL_FORM_STATE);
    const [formErrors, setFormErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (open) {
            if (rowData) {
                setFormValues({
                    name: rowData.display_name || '',
                    email: rowData.email || '',
                    mobile: rowData.mobile_number || '',
                    role: rowData.role || '',
                });
            } else {
                setFormValues(INITIAL_FORM_STATE);
            }
            setFormErrors({});
        }
    }, [open, rowData]);

    const handleInputChange = ({ target: { name, value } }) => {
        setFormValues((prev) => ({ ...prev, [name]: value }));
    };

    const validateFields = () => {
        const errors = {};
        const { email, mobile, role } = formValues;

        if (!role) errors.role = "Role is required*";

        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        if (!email) errors.email = "Email is required*";
        else if (!emailRegex.test(email)) errors.email = "Enter a valid email address*";

        const mobileRegex = /^[6-9][0-9]{9}$/;
        if (!mobile) errors.mobile = "Mobile is required*";
        else if (!mobileRegex.test(mobile)) errors.mobile = "Enter a valid 10-digit mobile number*";
        return errors;
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        const errors = validateFields();
        if (Object.keys(errors).length) {
            setFormErrors(errors);
            return;
        }

        const { name, email, mobile, role } = formValues;
        const payload = rowData
            ? { display_name: name, mobile_number: mobile, role }
            : {  email, mobile_number: mobile, role };

        setIsLoading(true);
        try {
            const response = rowData
                ? await editAdmin(rowData.ejet_admin_id, payload)
                : await createAdmin( payload);

            toast.success(response?.data?.message || "Success");
            onClose();
            getAllUsersData();
        } catch (error) {
            toast.error(error?.response?.data?.message || error?.message || "Something went wrong");
        } finally {
            setIsLoading(false);
        }
    };

    const renderSelectField = (label, name, options, labelsMap, disabled = false) => (
        <FormControl fullWidth size="small" error={!!formErrors[name]}>
            <InputLabel>{label}</InputLabel>
            <Select
                value={formValues[name]}
                onChange={handleInputChange}
                name={name}
                label={label}
                disabled={disabled}
            >
                {options.map((val) => (
                    <MenuItem key={val} value={val}>
                        {labelsMap?.[val] || val.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
                    </MenuItem>
                ))}
            </Select>
            {formErrors[name] && <FormHelperText>{formErrors[name]}</FormHelperText>}
        </FormControl>
    );

    return (
        <Modal open={open} onClose={onClose}>
            <Box
                sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: "45%",
                    bgcolor: "background.paper",
                    boxShadow: 24,
                    p: 4,
                    borderRadius: 2,
                }}
            >
                <Typography variant="h6" sx={{ mb: 2 }}>
                    {rowData ? "Edit Admin" : "Create Admin"}
                </Typography>
                <form onSubmit={handleSubmit}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            {renderSelectField("Select Role *", "role", roles, roleLabels)}
                        </Grid>
                        {rowData &&
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Name *"
                                name="name"
                                value={formValues.name}
                                onChange={handleInputChange}
                                error={!!formErrors.name}
                                helperText={formErrors.name}
                            />
                        </Grid>}
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Email *"
                                name="email"
                                value={formValues.email}
                                onChange={handleInputChange}
                                error={!!formErrors.email}
                                helperText={formErrors.email}
                                disabled={!!rowData}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Mobile *"
                                name="mobile"
                                value={formValues.mobile}
                                onChange={handleInputChange}
                                error={!!formErrors.mobile}
                                helperText={formErrors.mobile}
                            />
                        </Grid>
                    
                        <Grid item xs={12} sx={{ textAlign: "center" }}>
                            <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                                disabled={isLoading}
                                sx={{ mr: 2 }}
                            >
                                {isLoading ? <CircularProgress size={24} /> : rowData ? "Update" : "Create"}
                            </Button>
                            <Button variant="outlined" color="secondary" onClick={onClose}>
                                Cancel
                            </Button>
                        </Grid>
                    </Grid>
                </form>
            </Box>
        </Modal>
    );
};

export default EditAdminModal;
